import { Calendar, Mail, MapPin, MessageSquare, Phone, User } from 'lucide-react';
import React from 'react';

import { Pill } from '@/components/common/Pill';
import { cn } from '@/lib/utils';

import { InfoCard, InfoRow } from '../shared/InfoCard';
import { getPrerequisiteVariant } from '../stage-config';
import type { MiniTortClaimDetail, MiniTortSubmission, MiniTortSubmissionAttachment, PrerequisiteStatus } from '../types';
import { formatCurrency, formatFullDate, formatRelativeDate } from '../utils';

/* ------------------------------------------------------------------ */
/* Claimant info section                                               */
/* ------------------------------------------------------------------ */

export const ClaimantInfoSection: React.FC<{ claim: MiniTortClaimDetail; submission: MiniTortSubmission }> = ({ claim, submission }) => (
  <InfoCard title="Claimant Information">
    <div className="grid gap-4 sm:grid-cols-2">
      <InfoRow icon={<User className="h-4 w-4" />} label="Name" value={`${submission.claimant_first_name} ${submission.claimant_last_name}`} />
      <InfoRow icon={<Mail className="h-4 w-4" />} label="Email" value={submission.claimant_email} />
      <InfoRow icon={<Phone className="h-4 w-4" />} label="Phone" value={submission.claimant_phone} />
      <InfoRow icon={<MessageSquare className="h-4 w-4" />} label="Preferred Contact" value={claim.preferred_contact_method} />
    </div>
    <div className="mt-3 flex items-center gap-4 border-t border-zinc-100 pt-3 dark:border-zinc-800">
      <ConsentBadge label="Email" consented={claim.email_consent} />
      <ConsentBadge label="SMS" consented={claim.sms_consent} />
    </div>
  </InfoCard>
);

/* ------------------------------------------------------------------ */
/* Accident details section                                            */
/* ------------------------------------------------------------------ */

export const AccidentDetailsSection: React.FC<{ claim: MiniTortClaimDetail; submission: MiniTortSubmission }> = ({ claim, submission }) => (
  <InfoCard title="Accident Details">
    <div className="grid gap-4 sm:grid-cols-2">
      <InfoRow icon={<Calendar className="h-4 w-4" />} label="Accident Date" value={formatFullDate(submission.accident_date)} />
      <InfoRow icon={<MapPin className="h-4 w-4" />} label="Location" value={submission.accident_location} />
      <InfoRow label="At Fault" value={submission.at_fault} />
      <InfoRow label="Deductible Paid" value={submission.deductible_paid} />
      <InfoRow label="Damage Amount" value={formatCurrency(claim.damage_amount)} />
      <InfoRow label="Police Dept." value={submission.police_department} />
      <InfoRow label="Report #" value={submission.report_number} />
      <InfoRow label="Was Injured" value={submission.was_injured ? 'Yes' : 'No'} />
    </div>
    {claim.damage_description && (
      <div className="mt-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
        <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Damage Description</p>
        <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{claim.damage_description}</p>
      </div>
    )}
  </InfoCard>
);

/* ------------------------------------------------------------------ */
/* Coverage status section                                             */
/* ------------------------------------------------------------------ */

export const CoverageStatusSection: React.FC<{ claim: MiniTortClaimDetail }> = ({ claim }) => (
  <InfoCard title="Coverage Status">
    <p className="mb-3 text-xs text-zinc-500 dark:text-zinc-400">
      Both 1P (claimant&apos;s own insurance from declarations page) and 3P (at-fault party&apos;s insurance from police report) must be verified before generating a demand.
    </p>
    <div className="grid gap-4 sm:grid-cols-2">
      <CoverageCard title="1P Coverage (Claimant)" subtitle="From declarations page" status={claim.coverage_1p_status} source={claim.coverage_1p_source} confidence={claim.coverage_1p_confidence} satisfiedAt={claim.coverage_1p_satisfied_at} />
      <CoverageCard title="3P Coverage (At-Fault)" subtitle="From police report" status={claim.coverage_3p_status} source={claim.coverage_3p_source} confidence={claim.coverage_3p_confidence} satisfiedAt={claim.coverage_3p_satisfied_at} />
    </div>
    <div className="mt-4">
      <InfoRow label="Demand Letter" value={claim.demand_letter_status} />
    </div>
  </InfoCard>
);

/* ------------------------------------------------------------------ */
/* Police report section                                               */
/* ------------------------------------------------------------------ */

export const PoliceReportSection: React.FC<{ claim: MiniTortClaimDetail; submission: MiniTortSubmission | null }> = ({ claim, submission }) => (
  <InfoCard title="Police Report">
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Status</p>
        <div className="mt-1 flex items-center gap-2">
          <Pill variant={getPrerequisiteVariant(claim.police_report_status)} size="sm">
            {claim.police_report_status}
          </Pill>
        </div>
      </div>
      {claim.police_report_source && <InfoRow label="Source" value={claim.police_report_source} />}
      {claim.police_report_satisfied_at && <InfoRow label="Satisfied" value={formatRelativeDate(claim.police_report_satisfied_at)} />}
      {submission?.report_number && <InfoRow label="Report #" value={submission.report_number} />}
      {submission?.police_department && <InfoRow label="Department" value={submission.police_department} />}
      {claim.police_report_id && <InfoRow label="Record ID" value={`${claim.police_report_id.slice(0, 12)}...`} />}
    </div>
  </InfoCard>
);

/* ------------------------------------------------------------------ */
/* Attachments section                                                 */
/* ------------------------------------------------------------------ */

export const AttachmentsSection: React.FC<{ attachments: MiniTortSubmissionAttachment[] }> = ({ attachments }) => (
  <InfoCard title="Submission Attachments">
    <div className="space-y-2">
      {attachments.map((att) => (
        <div key={att.id} className="flex items-center justify-between rounded-md border border-zinc-100 px-3 py-2 dark:border-zinc-800">
          <div>
            <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{att.original_filename ?? att.attachment_type}</p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {att.attachment_type}
              {att.file_size_bytes ? ` \u00b7 ${(att.file_size_bytes / 1024).toFixed(0)} KB` : ''}
            </p>
          </div>
          <Pill variant={getAttachmentVariant(att.status)} size="sm">
            {att.status}
          </Pill>
        </div>
      ))}
    </div>
  </InfoCard>
);

/* ------------------------------------------------------------------ */
/* Eligibility section                                                 */
/* ------------------------------------------------------------------ */

export const EligibilitySection: React.FC<{ submission: MiniTortSubmission }> = ({ submission }) => (
  <InfoCard title="Eligibility Details">
    <div className="grid gap-4 sm:grid-cols-2">
      <InfoRow label="Registered Owner & Named Insured" value={submission.is_registered_owner_and_named_insured ? 'Yes' : 'No'} />
      <InfoRow label="Accident Within Last Year" value={submission.accident_within_last_year ? 'Yes' : 'No'} />
      <InfoRow label="Authorized MAL to Obtain Records" value={submission.authorized_mal_to_obtain ? 'Yes' : 'No'} />
      <InfoRow label="Wants Attorney Consult" value={submission.wants_attorney_consult ? 'Yes' : 'No'} />
      <InfoRow label="Source" value={submission.source} />
      <InfoRow label="External ID" value={submission.external_submission_id} />
    </div>
  </InfoCard>
);

/* ------------------------------------------------------------------ */
/* Requested materials section                                         */
/* ------------------------------------------------------------------ */

export const RequestedMaterialsSection: React.FC<{ prompt: string }> = ({ prompt }) => (
  <InfoCard title="Requested Materials">
    <p className="text-sm text-zinc-700 dark:text-zinc-300">{prompt}</p>
  </InfoCard>
);

/* ------------------------------------------------------------------ */
/* Shared helpers                                                      */
/* ------------------------------------------------------------------ */

const ConsentBadge: React.FC<{ label: string; consented: boolean }> = ({ label, consented }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium',
      consented
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
        : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400',
    )}
  >
    {label}: {consented ? 'Yes' : 'No'}
  </span>
);

const CoverageCard: React.FC<{
  title: string;
  subtitle?: string;
  status: PrerequisiteStatus;
  source: string | null;
  confidence: number | null;
  satisfiedAt: string | null;
}> = ({ title, subtitle, status, source, confidence, satisfiedAt }) => (
  <div className="rounded-md border border-zinc-100 p-3 dark:border-zinc-800">
    <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{title}</p>
    {subtitle && <p className="text-[10px] text-zinc-400 dark:text-zinc-500">{subtitle}</p>}
    <div className="mt-1 flex items-center gap-2">
      <Pill variant={getPrerequisiteVariant(status)} size="sm">{status}</Pill>
    </div>
    {source && <p className="mt-1 text-xs text-zinc-500">Source: {source}</p>}
    {confidence != null && <p className="text-xs text-zinc-500">Confidence: {(confidence * 100).toFixed(0)}%</p>}
    {satisfiedAt && <p className="text-xs text-zinc-500">Satisfied: {formatRelativeDate(satisfiedAt)}</p>}
  </div>
);

function getAttachmentVariant(status: string) {
  if (status === 'completed') return 'green' as const;
  if (status === 'failed') return 'red' as const;
  return 'gray' as const;
}
